import { useEffect, useMemo, useState } from 'react';
import { usePaginationAndSort } from '@restore/helpers';
import { debounce } from 'lodash';
import { useSiteSubjectsListSlice } from '@restore/SiteSubjectsList/SiteSubjectsList.slice';
import { useParams } from 'react-router-dom';

export type Filters = {
  patientCode: string;
};

export const useSubjectListFilters = () => {
  const [patientCode, setPatientCode] = useState<string>('');

  const { id: clinicalTrialId, siteId } = useParams<{
    id: string;
    siteId: string;
  }>();

  const [search, fetchSiteSubjectsList, setSearch, newlyCreatedSubjectCode] =
    useSiteSubjectsListSlice((state, actions) => [
      state.search,
      actions.fetchSiteSubjectsList,
      actions.setSearchValue,
      state.newlyCreatedSubjectCode,
    ]);

  const { setSortBy, sortBy, currentPage, currentPageSize, setCurrentPage } =
    usePaginationAndSort('subjectList');

  const onSort = (name: string, dir?: string) => {
    const sort = dir ? `${name},${dir}` : undefined;
    setSortBy({ listName: 'subjectList', newSortBy: sort });
  };

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        if ((value && value.length > 1) || value === '') {
          setCurrentPage({ listName: 'subjectList', newPage: 1 });
          setSearch(value);
        }
      }, 500),
    [patientCode]
  );

  useEffect(() => {
    if (clinicalTrialId && siteId) {
      fetchSiteSubjectsList({
        siteId,
        clinicalTrialId,
        args: {
          patientCode: search,
          sort: sortBy,
          page: currentPage,
          size: currentPageSize,
        },
      });
    }
  }, [
    clinicalTrialId,
    siteId,
    search,
    sortBy,
    currentPage,
    currentPageSize,
    newlyCreatedSubjectCode,
  ]);

  return {
    patientCode,
    setPatientCode,
    onSort,
    onSearch,
    setSortBy,
    sortBy,
    currentPage,
    currentPageSize,
    setCurrentPage,
  };
};
